import styled from 'styled-components'
import { getRetailerCouponUrl, routes } from '@shared/constants/routes'
import { DISPLAY_FORMAT } from '@/features/shared/constants/analytics'
import { Progress, ProgressBar } from '@/features/shared/components/Progress'
import { getCouponDetails } from '@/features/shop/utils/normalizeCoupon'
import { type Product } from '@/features/shop/services/Product/types'
import { CouponProgressCircles } from '@/features/shop/components/CouponProgressCircles'
import { PositiveCircleFillIcon } from '@shipt/design-system-icons'
import { ScrollIntoView } from '@/features/shared/components/ScrollIntoView'
import {
  getCouponPromoCardProperties,
  trackPromoCardViewed,
} from '@/features/shared/analytics/promoCard'
import { Button, TextButton } from '@shipt/design-system-buttons'
import { TextButtonLink } from '@/features/shared/elements/Link/Buttons'
import { Body } from '@shipt/design-system-typography'
import { VDivider } from '@/features/shared/components/Dividers'
import { useIsOrder } from '@/features/shop/state/SelectedActiveOrder/hooks'
import {
  type CouponClippedState,
  useClipCouponAndCalculateSavings,
} from '@/features/shop/services/Coupons/hooks'
import { getErrorMessage } from '@/features/shared/utils/dataFetching/utils'
import { useUpdateProductQuantity } from '@/features/shared/hooks/useUpdateProductQuantity'
import { useHandleDialog } from '@/features/shared/state/Dialog/useHandleDialog'
import { useIsGuestWithZipOnly } from '@/features/shop/components/ProductDetail/PLA/hooks'
import { DynamicCouponTermsModal } from '@/features/shop/components/Modal/CouponTermsModal/DynamicCouponTermsModal'
import { Row, Stack } from '@shipt/design-system-layouts'
import { usePathWithSearch } from '@/features/shared/hooks/routes/usePathWithSearch'
import { useIsGuestUser, useUserStoreName } from '@/features/account/services/User/hooks'
import { useSegwayRouter } from '@/features/shared/hooks/useSegwayRouter'

type Props = {
  coupon: {
    couponId: string
    description?: string
    summary?: string
    expiredAt: string
    faceValue: number
    appliedProductQty: number
  }
  product: Product
  productQuantity: number
  couponClippedState: CouponClippedState
}

export const ProductDetailCoupon = ({
  coupon,
  couponClippedState,
  product,
  productQuantity = 0,
}: Props) => {
  const isOrder = useIsOrder()
  const { updateProductQuantity } = useUpdateProductQuantity()
  const {
    error,
    clipCouponAndCalculateSavings,
    isPending: isClippingCoupon,
  } = useClipCouponAndCalculateSavings()
  const { openDialog } = useHandleDialog()
  const isGuestWithZipOnly = useIsGuestWithZipOnly()
  const isGuestUser = useIsGuestUser()
  const storeName = useUserStoreName()
  const exactPath = usePathWithSearch()
  const encodedFullPath = encodeURIComponent(exactPath)
  const router = useSegwayRouter()

  const { isClipped, isFulfilled, quantityNeeded, completionPercent } = couponClippedState
  const { couponId = '', description = '', appliedProductQty = 0 } = coupon
  if (!couponId) return null
  const { formattedValue, formattedSummary, hasMinimumQtyReq } = getCouponDetails(coupon)
  const showProgressBar = appliedProductQty > 3 && isClipped
  const showProgressCircles = hasMinimumQtyReq && appliedProductQty <= 3 && isClipped
  const summaryText = hasMinimumQtyReq ? formattedSummary : `Save ${formattedValue}`
  const clippedCouponLinkText = hasMinimumQtyReq ? 'Mix and match' : 'See all eligible items'
  const addToContext = isOrder ? 'Order' : 'Cart'
  const addQtyButtonText = `Add ${quantityNeeded} to ${addToContext}`
  const couponPromoCardEventProperties = getCouponPromoCardProperties({
    coupon,
    displayFormat: DISPLAY_FORMAT.DETAIL,
    actionType: 'coupon',
  })
  const isViewOnly = isClipped || isGuestWithZipOnly

  const handleOpenTermsModal = () => {
    openDialog(DynamicCouponTermsModal, { coupon })
  }

  const handleAddQtyButton = () => {
    return updateProductQuantity({
      product,
      quantity: productQuantity + quantityNeeded,
      itemUpdated: Boolean(productQuantity),
    })
  }

  const handleClipCoupon = () => {
    if (isGuestUser) {
      router.push(`${routes.SIGN_UP.url}?next=${encodedFullPath}`)
    } else {
      clipCouponAndCalculateSavings({
        couponId: coupon.couponId,
        summary: coupon.summary,
      })
    }
  }

  return (
    <ScrollIntoView
      onInView={() => {
        trackPromoCardViewed(couponPromoCardEventProperties)
      }}
    >
      <Wrapper $isViewOnly={isViewOnly} data-show-progress-bar={showProgressBar}>
        {isFulfilled ? (
          <Row justify="space-between">
            <Body strong concept="feedback" feedbackType="promotion">
              -{formattedValue} applied at checkout
            </Body>
            {!!showProgressBar && (
              <PositiveCircleFillIcon concept="feedback" feedbackType="promotion" />
            )}
          </Row>
        ) : (
          <Row justify="space-between">
            <Stack direction="column" spacing="xs">
              <Body strong concept="feedback" feedbackType="promotion">
                {summaryText}
              </Body>
              <Body size="md" variant="secondary">
                {description}
              </Body>
            </Stack>
            {!isViewOnly && (
              <ClipButton
                size="sm"
                loading={isClippingCoupon}
                onClick={handleClipCoupon}
                aria-label="clip coupon"
              >
                Clip
              </ClipButton>
            )}
          </Row>
        )}
        <PrimaryTextButtonWrappers
          applyPadding={(showProgressBar && !isFulfilled) || showProgressCircles}
        >
          <TextButton onClick={handleOpenTermsModal} aria-label="open to view coupon terms">
            Coupon terms
          </TextButton>
          {!!isClipped && (
            <>
              <Divider />
              <TextButtonLink
                href={`${getRetailerCouponUrl(storeName, couponId)}?productId=${product.id}`}
              >
                {clippedCouponLinkText}
              </TextButtonLink>
            </>
          )}
        </PrimaryTextButtonWrappers>
        {!!showProgressCircles && (
          <CouponProgressCircles coupon={coupon} couponClippedState={couponClippedState} />
        )}
        {!!showProgressBar && !isFulfilled && (
          <Button size="sm" onClick={handleAddQtyButton} aria-label={addQtyButtonText}>
            {addQtyButtonText}
          </Button>
        )}
      </Wrapper>
      {!!showProgressBar && (
        <PromotionProgress>
          <PromotionProgressBar value={completionPercent} min={0} max={100} />
        </PromotionProgress>
      )}
      {Boolean(error) && (
        <ErrorMessage aria-live="assertive">{getErrorMessage(error)}</ErrorMessage>
      )}
    </ScrollIntoView>
  )
}

const Wrapper = styled.div<{
  $isViewOnly: boolean
  'data-show-progress-bar': boolean
}>`
  padding: 16px;
  margin: 0;
  border: 2px solid;
  border-radius: 8px;
  border-style: ${({ $isViewOnly }) => !$isViewOnly && 'dashed'};
  border-color: ${({ $isViewOnly, theme }) => ($isViewOnly ? 'transparent' : theme.orange700)};
  background-color: ${({ $isViewOnly, theme }) => ($isViewOnly ? theme.gray50 : theme.white)};

  &[data-show-progress-bar='true'] {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }
`

const PrimaryTextButtonWrappers = styled.div<{ applyPadding: boolean }>`
  display: flex;
  align-items: center;
  padding-bottom: ${({ applyPadding }) => (applyPadding ? '12px' : 0)};
`

const Divider = styled(VDivider)`
  height: 16px;
`

const ClipButton = styled(Button)`
  align-self: flex-start;
`
const PromotionProgress = styled(Progress)`
  border-top-left-radius: 0;
  border-top-right-radius: 0;
  background-color: ${({ theme }) => theme.gray100};
`

const PromotionProgressBar = styled(ProgressBar)`
  height: 0.375rem;
  background: ${({ theme }) => `linear-gradient(to right, ${theme.orange500}, ${theme.red500})`};
`

const ErrorMessage = styled.div`
  text-align: center;
  margin-top: 16px;
  color: ${({ theme }) => theme.red500};
`
